import { Link } from 'react-router-dom';
import { CheckCircle2, Circle, X, Rocket } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useOnboarding } from '@/hooks/useOnboarding';

type Step = {
  key: keyof ReturnType<typeof useOnboarding>['state'];
  label: string;
  cta: string;
  href: string;
};

const STEPS: Step[] = [
  { key: 'created_first_deal_at',   label: 'Capture your first up — name + vehicle is enough', cta: 'Quick capture', href: '/rep' },
  { key: 'logged_first_activity_at', label: 'Log one thing you just did',                       cta: 'Log it',        href: '/log-activity' },
  { key: 'set_first_next_step_at',  label: 'Set the next step so it never falls through',       cta: 'Open a deal',   href: '/rep' },
  { key: 'viewed_leaderboard_at',   label: 'See where you sit on the floor',                    cta: 'Leaderboard',   href: '/leaderboard' },
  { key: 'enabled_notifications_at', label: 'Turn on alerts so hot ups find you',                cta: 'Settings',      href: '/settings' },
];

export function OnboardingChecklist() {
  const { state, completedCount, totalSteps, dismiss, visible } = useOnboarding();
  if (!visible) return null;

  const pct = Math.round((completedCount / totalSteps) * 100);

  return (
    <Card className="border-primary/30 bg-gradient-to-br from-primary/5 to-transparent">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-8 h-8 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
              <Rocket className="h-4 w-4 text-primary" />
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-sm">Don't remember anything. Just log it.</p>
              <p className="text-xs text-muted-foreground">{completedCount} of {totalSteps} done · 60-second setup</p>
            </div>
          </div>
          <button
            onClick={dismiss}
            aria-label="Dismiss checklist"
            className="text-muted-foreground hover:text-foreground transition-colors p-1 -m-1"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="w-full h-1.5 rounded-full bg-muted overflow-hidden">
          <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
        </div>

        <ul className="space-y-1.5">
          {STEPS.map(step => {
            const done = !!state[step.key];
            return (
              <li key={step.key} className="flex items-center justify-between gap-2 text-sm">
                <div className="flex items-center gap-2 min-w-0">
                  {done
                    ? <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                    : <Circle className="h-4 w-4 text-muted-foreground shrink-0" />}
                  <span className={done ? 'line-through text-muted-foreground' : ''}>{step.label}</span>
                </div>
                {!done && (
                  <Button asChild size="sm" variant="ghost" className="h-7 px-2 text-xs shrink-0">
                    <Link to={step.href}>{step.cta}</Link>
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}
